import { useEffect, useRef } from "react";

import type { PositionFormik, SessionDates } from "./positionForm.types";
import {
  applyCalendarSelection,
  createEmptyShiftRow,
  getSessionDateKeys,
} from "./positionForm.utils";

/** Like useEffect, but skips the first run (the mount). */
const useUpdateEffect = (effect: () => void, deps: unknown[]) => {
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    effect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

/**
 * Keeps the shift rows (`positionTimeInfo`) in sync with the "each date of the
 * session" checkbox, the calendar selection, the view switch and the session
 * dates. Only used by the add flow; the edit flow edits a single shift.
 */
const useShiftScheduleSync = (
  { values, setValues, touched, setTouched }: PositionFormik,
  session: SessionDates,
) => {
  const resetRowsTouched = () => setTouched?.({ ...(touched || {}), positionTimeInfo: [] }, false);

  // "Add positions for each date of the session" was toggled.
  useUpdateEffect(() => {
    const sessionDates = getSessionDateKeys(session);

    if (values?.currentView === "calendar") {
      if (values?.eachDateOfSession) {
        setValues((prev) => applyCalendarSelection(prev, sessionDates));
      } else if (values?.eachDateOfSessionChangedFromInput) {
        setValues((prev) => applyCalendarSelection(prev, []));
      }
    } else {
      setValues((prev) => ({
        ...prev,
        positionTimeInfo: prev?.eachDateOfSession
          ? sessionDates.map((day) => createEmptyShiftRow({ dateStart: day }))
          : [createEmptyShiftRow()],
      }));
    }

    resetRowsTouched();
  }, [values?.eachDateOfSession]);

  // Calendar <-> list view switch. The rows themselves are reset by the caller.
  useUpdateEffect(() => {
    setValues((prev) => ({
      ...prev,
      eachDateOfSession: false,
      eachDateOfSessionChangedFromInput: false,
      detachEachDateOfSessionInput: false,
      calendarViewDates: [],
    }));
    resetRowsTouched();
  }, [values?.currentView]);

  // Picking every date on the calendar is the same as ticking "each date".
  useUpdateEffect(() => {
    const totalDates = getSessionDateKeys(session).length;
    setValues((prev) => ({
      ...prev,
      eachDateOfSession: prev?.calendarViewDates?.length === totalDates,
      eachDateOfSessionChangedFromInput: false,
    }));
  }, [values?.calendarViewDates?.length]);

  // Session dates changed while "each date" is on: re-select the new range.
  useEffect(() => {
    if (values?.currentView === "calendar" && values?.eachDateOfSession) {
      setValues((prev) => applyCalendarSelection(prev, getSessionDateKeys(session)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.sessionStart, session?.sessionEnd]);
};

export default useShiftScheduleSync;
